import { defineStore } from 'pinia';
import { ref, reactive } from 'vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  getMatterRecipients,
  createMatterRecipient as createMatterRecipientApi,
  updateMatterRecipient as updateMatterRecipientApi,
  deleteMatterRecipient as deleteMatterRecipientApi,
} from '@/services/settlement/SettlementRecipientsService';
// Reuses the row/request shapes already declared for Step 3's auto-populate read (same resource, one source of truth).
import type {
  MatterRecipientData as MatterRecipient,
  CreateMatterRecipientRequest,
  UpdateMatterRecipientRequest,
} from '@/models/settlementTypes';

interface FeeRecipientsUiState {
  loading: boolean;
  error: unknown;
}

export const useFeeRecipientsStore = defineStore('feeRecipientsStore', () => {
  const { handleError } = useSnackbarHandler();

  // State
  const recipients = ref<MatterRecipient[]>([]);
  const isLocked = ref<boolean>(false);
  const uiState: FeeRecipientsUiState = reactive({ loading: false, error: null });

  // Actions

  /** Re-fetches on every call — this tab does not cache across mounts (P3.2 AC). */
  async function fetchRecipients(matterId: string): Promise<void> {
    uiState.loading = true;
    uiState.error = null;
    try {
      const data = await getMatterRecipients(matterId);
      recipients.value = data.recipients ?? [];
      isLocked.value = data.isLocked;
    } catch (e: unknown) {
      uiState.error = e;
      handleError(e);
    } finally {
      uiState.loading = false;
    }
  }

  /** Creates one recipient and appends the response row locally (no bulk endpoint to re-fetch from).
   *  Errors are left uncaught so the caller can show them inline instead of as a generic toast. */
  async function createRecipient(
    matterId: string,
    payload: CreateMatterRecipientRequest,
  ): Promise<MatterRecipient> {
    const created = await createMatterRecipientApi(matterId, payload);
    recipients.value = [...recipients.value, created];
    return created;
  }

  /** Updates one recipient and replaces that row in local state from the response. */
  async function updateRecipient(
    matterId: string,
    recipientId: string,
    payload: UpdateMatterRecipientRequest,
    confirmReset?: boolean,
  ): Promise<MatterRecipient> {
    const updated = await updateMatterRecipientApi(matterId, recipientId, payload, confirmReset);
    const idx = recipients.value.findIndex(
      (r) => r.matterSettlementRecipientId === recipientId,
    );
    if (idx !== -1) {
      recipients.value = [
        ...recipients.value.slice(0, idx),
        updated,
        ...recipients.value.slice(idx + 1),
      ];
    }
    return updated;
  }

  /** Tiered per VR-013 — left uncaught so the caller can distinguish 409/400; removes locally only on 204. */
  async function deleteRecipient(
    matterId: string,
    recipientId: string,
    confirmReset?: boolean,
  ): Promise<void> {
    await deleteMatterRecipientApi(matterId, recipientId, confirmReset);
    recipients.value = recipients.value.filter(
      (r) => r.matterSettlementRecipientId !== recipientId,
    );
  }

  function $reset(): void {
    recipients.value = [];
    isLocked.value = false;
    uiState.loading = false;
    uiState.error = null;
  }

  return {
    recipients,
    isLocked,
    uiState,
    fetchRecipients,
    createRecipient,
    updateRecipient,
    deleteRecipient,
    $reset,
  };
});
